import { useEffect, useState } from "react";
import { platformApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FileText, Loader2 } from "lucide-react";

const DARK_CARD = "bg-slate-900 border border-slate-800 rounded-[8px] p-5";

const FIELD_DEFS = [
  ["header_markdown",       "Header markdown",        "textarea", 4],
  ["body_intro_markdown",   "Body — intro markdown",  "textarea", 6],
  ["body_outro_markdown",   "Body — outro markdown",  "textarea", 6],
  ["payment_terms",         "Payment terms",          "textarea", 3],
  ["bank_details",          "Bank details",           "textarea", 4],
  ["footer_markdown",       "Footer markdown",        "textarea", 3],
  ["accent_color",          "Accent color (CSS)",     "text"],
  ["logo_position",         "Logo position",          "text"],
];

export default function AdminInvoiceTemplate() {
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await platformApi.get("/platform/invoice-template");
      setTpl(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        FIELD_DEFS.map(([k]) => [k, tpl[k] ?? ""])
          .concat([
            ["show_vat_block",     !!tpl.show_vat_block],
            ["show_extra_fields",  !!tpl.show_extra_fields],
          ])
      );
      const { data } = await platformApi.put("/platform/invoice-template", payload);
      setTpl(data);
      toast.success("Standard invoice template saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading || !tpl) {
    return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }

  const setField = (k, v) => setTpl((t) => ({ ...t, [k]: v }));

  return (
    <div className="space-y-6" data-testid="admin-invoice-template-page">
      <div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-rose-400">// Platform-wide</div>
        <h1 className="text-3xl font-black mt-1 flex items-center gap-3">
          <FileText className="h-7 w-7 text-rose-400" /> Standard invoice template
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          This is the read-only "Standard" entry that appears at the top of every tenant's
          invoice template picker. Edits here apply to all tenants who haven't created their own
          custom template. Version {tpl.version}, last edited by {tpl.updated_by || "—"}.
        </p>
      </div>

      <div className={DARK_CARD}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELD_DEFS.map(([key, label, type, rows]) => (
            <div key={key} className={type === "textarea" ? "md:col-span-2" : ""}>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">{label}</Label>
              {type === "textarea" ? (
                <Textarea
                  value={tpl[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  rows={rows || 3}
                  className="bg-slate-800 border-slate-700 text-slate-100 font-mono text-xs"
                  data-testid={`admin-inv-tpl-${key}`}
                />
              ) : (
                <Input
                  value={tpl[key] || ""}
                  onChange={(e) => setField(key, e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100"
                  data-testid={`admin-inv-tpl-${key}`}
                />
              )}
            </div>
          ))}
          <div className="flex items-center gap-3">
            <Switch
              checked={!!tpl.show_vat_block}
              onCheckedChange={(v) => setField("show_vat_block", !!v)}
              data-testid="admin-inv-tpl-show_vat_block"
            />
            <Label className="text-sm">Show VAT block</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={!!tpl.show_extra_fields}
              onCheckedChange={(v) => setField("show_extra_fields", !!v)}
              data-testid="admin-inv-tpl-show_extra_fields"
            />
            <Label className="text-sm">Show extra fields</Label>
          </div>
        </div>
        <div className="pt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={load} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-rose-600 hover:bg-rose-700" data-testid="admin-inv-tpl-save">
            {saving ? "Saving…" : "Save standard template"}
          </Button>
        </div>
      </div>
    </div>
  );
}
